import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fontSize as fs, textColor as tc } from '@/design/tokens';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
} from 'recharts';
import seasonsData from '@/data/cb/seasons.json';

const DISCORD_URL = 'https://discord.com/invite/29gn3mXqtA';
const SITE_URL = 'https://tfd.rocks';

const GOLD = '#c9a24b';
const GOLD_LIGHT = '#ecd28b';
const TEAL = '#4fb3a5';
const TEAL_LIGHT = '#6fd3c4';

// ── pre-compute headline stats ────────────────────────────────────────────────
const seasons = seasonsData.seasons;
const totalBattles = seasons.reduce((s, x) => s + x.battles, 0);
const totalDamage = seasons.reduce((s, x) => s + x.totalDamage, 0);
const peakParticipation = seasons.reduce(
  (best, x) => (x.players > best.players ? x : best),
  seasons[0],
);
const peakBattlesSeason = seasons.reduce(
  (best, x) => (x.battles > best.battles ? x : best),
  seasons[0],
);
const peakAvgDmgSeason = seasons.reduce(
  (best, x) => (x.avgDamagePerBattle > best.avgDamagePerBattle ? x : best),
  seasons[0],
);
// S25 is the shortest season — use it to drive the outlier marker
const shortestSeason = seasons.reduce(
  (best, x) => (x.durationDays < best.durationDays ? x : best),
  seasons[0],
);

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

// Clan Battles is 7-v-7, so each real battle is logged by up to 14 captains.
// The raw `battles` field is the summed per-captain counter; divide to estimate
// the number of battles actually fought. Hidden profiles mean this is a
// conservative figure (~95% of the true total).
const PLAYERS_PER_BATTLE = 14;
const estBattles = (rawBattles: number) => rawBattles / PLAYERS_PER_BATTLE;

// None of the figures on this page are exact — every one is reconstructed from
// the public profiles of EU captains, so we round to roughly two significant
// figures and prefix with ≈. The rounding and the symbol both signal "about
// this many," never a precise count.
function fmtApprox(n: number): string {
  if (n >= 1_000_000_000_000) return '≈' + +(n / 1_000_000_000_000).toFixed(1) + 'T';
  if (n >= 1_000_000_000) return '≈' + +(n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return '≈' + +(n / 1_000_000).toFixed(1) + 'M';
  if (n >= 10_000) return '≈' + Math.round(n / 1_000) + 'K';
  if (n >= 1_000) return '≈' + +(n / 1_000).toFixed(1) + 'K';
  if (n >= 100) return '≈' + Math.round(n / 10) * 10;
  return '≈' + Math.round(n);
}

function fmtDateRange(startDate: string, finishDate: string): string {
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  return `${fmt(startDate)} – ${fmt(finishDate)}`;
}

function tierLabel(min: number, max: number): string {
  if (min === max) return `Tier ${min}`;
  return `Tier ${min}–${max}`;
}

// ── EU flag SVG (self-hosted inline) ─────────────────────────────────────────
const EUFlag = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 14"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="European Union flag"
    style={{ borderRadius: 2, verticalAlign: 'middle', flexShrink: 0 }}
  >
    <rect width="20" height="14" fill="#003399" />
    {/* 12 stars in a ring, radius ~4.2 units centred at 10,7 */}
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      const cx = 10 + 4.2 * Math.cos(angle);
      const cy = 7 + 4.2 * Math.sin(angle);
      // 5-pointed star, tiny
      const pts = Array.from({ length: 5 }).map((_, j) => {
        const a = (j * 72 - 90) * (Math.PI / 180);
        const ai = ((j * 72 + 36) - 90) * (Math.PI / 180);
        const or = 0.72, ir = 0.31;
        return `${cx + or * Math.cos(a)},${cy + or * Math.sin(a)} ${cx + ir * Math.cos(ai)},${cy + ir * Math.sin(ai)}`;
      });
      return <polygon key={i} points={pts.join(' ')} fill="#FFCC00" />;
    })}
  </svg>
);

// ── corner-bracket panel decorator ───────────────────────────────────────────
const Brackets = ({ color = GOLD, opacity = 0.6 }: { color?: string; opacity?: number }) => (
  <>
    <span aria-hidden="true" style={{ position: 'absolute', top: 12, left: 12, width: 16, height: 16, borderTop: `1.5px solid ${color}`, borderLeft: `1.5px solid ${color}`, opacity }} />
    <span aria-hidden="true" style={{ position: 'absolute', top: 12, right: 12, width: 16, height: 16, borderTop: `1.5px solid ${color}`, borderRight: `1.5px solid ${color}`, opacity }} />
    <span aria-hidden="true" style={{ position: 'absolute', bottom: 12, left: 12, width: 16, height: 16, borderBottom: `1.5px solid ${color}`, borderLeft: `1.5px solid ${color}`, opacity }} />
    <span aria-hidden="true" style={{ position: 'absolute', bottom: 12, right: 12, width: 16, height: 16, borderBottom: `1.5px solid ${color}`, borderRight: `1.5px solid ${color}`, opacity }} />
  </>
);

// ── custom tooltip for charts ─────────────────────────────────────────────────
interface SeasonEntry {
  season: number;
  name: string;
  startDate: string;
  finishDate: string;
  durationDays: number;
  battles: number;
  players: number;
  wins: number;
  losses: number;
  winrate: number;
  totalDamage: number;
  avgDamagePerBattle: number;
  avgGamesPerPlayer: number;
  maxBattlesOnePlayer: number;
  tierMin: number;
  tierMax: number;
  year: number;
  label?: string;
  battlesK?: number;
}

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
  unit?: string;
  payload?: SeasonEntry;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  formatters?: Record<string, (v: number) => string>;
}

// Rich tooltip — shows full season context on every chart hover
const BrandTooltip = ({ active, payload, label, formatters }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload as SeasonEntry | undefined;
  const isShort = entry && entry.season === shortestSeason.season;
  return (
    <div style={{
      background: 'linear-gradient(180deg,rgba(14,24,26,.97),rgba(6,11,14,.98))',
      border: `1px solid rgba(201,162,75,.35)`,
      borderRadius: 6,
      padding: '12px 16px',
      fontFamily: "'Barlow',sans-serif",
      fontSize: fs.caption,
      boxShadow: '0 12px 40px rgba(0,0,0,.6)',
      minWidth: 220,
      maxWidth: 280,
    }}>
      {entry ? (
        <>
          <div style={{ color: tc.gold, fontWeight: 700, marginBottom: 2, letterSpacing: '.06em', fontSize: fs.small }}>
            Season {entry.season} · {entry.name}
          </div>
          <div style={{ color: tc.muted, fontSize: fs.caption, marginBottom: 1 }}>
            {fmtDateRange(entry.startDate, entry.finishDate)}
          </div>
          <div style={{ color: tc.teal, fontSize: fs.caption, marginBottom: isShort ? 4 : 8 }}>
            {tierLabel(entry.tierMin, entry.tierMax)} · {entry.durationDays}d season
          </div>
          {isShort && (
            <div style={{ color: tc.gold, fontSize: fs.caption, fontStyle: 'italic', marginBottom: 8, lineHeight: 1.4 }}>
              Short season — ~{entry.durationDays}d vs usual 41–70d
            </div>
          )}
          {/* Chart-specific data point(s) */}
          {payload.map((p) => (
            <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: tc.default, marginBottom: 3 }}>
              <span style={{ color: p.color }}>{p.name}</span>
              <span style={{ fontWeight: 600, color: tc.strong }}>
                {formatters?.[p.name] ? formatters[p.name](p.value) : p.value.toLocaleString()}
              </span>
            </div>
          ))}
          {/* Full season context — always shown */}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(201,162,75,.15)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px' }}>
              {[
                { label: 'Battles', value: fmtApprox(estBattles(entry.battles)), color: GOLD },
                { label: 'Captains', value: fmtApprox(entry.players), color: TEAL },
                { label: 'Avg Games', value: fmtApprox(entry.avgGamesPerPlayer), color: tc.default },
                { label: 'Avg Damage', value: fmtApprox(entry.avgDamagePerBattle), color: tc.default },
                { label: 'Win Rate', value: `${entry.winrate}%`, color: tc.default },
                { label: 'Max Battles', value: fmtApprox(entry.maxBattlesOnePlayer), color: tc.muted },
              ].map((stat) => (
                <div key={stat.label}>
                  <div style={{ fontSize: fs.caption, letterSpacing: '.14em', textTransform: 'uppercase', color: tc.muted, marginBottom: 1 }}>{stat.label}</div>
                  <div style={{ fontSize: fs.caption, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={{ color: tc.gold, fontWeight: 700, marginBottom: 6, letterSpacing: '.06em' }}>
          Season {label}
        </div>
      )}
    </div>
  );
};

// ── section divider ───────────────────────────────────────────────────────────
const Divider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '0 auto 48px', maxWidth: 400 }}>
    <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.4))' }} />
    <span style={{ width: 6, height: 6, transform: 'rotate(45deg)', background: GOLD, boxShadow: '0 0 8px rgba(201,162,75,.6)' }} />
    <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,rgba(201,162,75,.4),transparent)' }} />
  </div>
);

// ── chart data with season label ──────────────────────────────────────────────
const chartData: SeasonEntry[] = seasons.map((s) => ({
  ...s,
  label: `S${s.season}`,
  battlesK: Math.round(estBattles(s.battles) / 1000),
}));

// ── DiscordIcon ───────────────────────────────────────────────────────────────
const DiscordIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.6 5.6A17 17 0 0 0 15.3 4.3l-.2.4a13 13 0 0 1 3.7 1.9 14.6 14.6 0 0 0-13.6 0A13 13 0 0 1 8.9 4.7l-.2-.4A17 17 0 0 0 4.4 5.6 18.4 18.4 0 0 0 1.3 18a17.6 17.6 0 0 0 5.3 2.7l.4-.6a11.4 11.4 0 0 1-1.8-.9l.4-.3a12.6 12.6 0 0 0 10.8 0l.4.3c-.6.4-1.2.6-1.8.9l.4.6A17.6 17.6 0 0 0 22.7 18a18.4 18.4 0 0 0-3.1-12.4ZM8.5 15.3c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.9 2.1-1.9 2.1Zm7 0c-1 0-1.9-1-1.9-2.1s.8-2.1 1.9-2.1 1.9 1 1.9 2.1-.9 2.1-1.9 2.1Z" />
  </svg>
);

// ── S25 outlier reference line label — now with context ───────────────────────
const S25Label = ({ viewBox }: { viewBox?: { x?: number; y?: number; height?: number } }) => {
  const x = (viewBox?.x ?? 0) + 5;
  const y = (viewBox?.y ?? 0) + 14;
  return (
    <text x={x} y={y} fill={tc.gold} fontSize={fs.caption} fontFamily="'Barlow',sans-serif">
      S25 · {shortestSeason.durationDays}d season
    </text>
  );
};

// ── Tuned-season needle cap — small teal diamond echoing the tuner dial ───────
const TunedNeedleLabel = ({ viewBox }: { viewBox?: { x?: number; y?: number } }) => {
  const x = viewBox?.x ?? 0;
  const y = (viewBox?.y ?? 0) + 1;
  return (
    <polygon
      points={`${x},${y - 5} ${x + 5},${y} ${x},${y + 5} ${x - 5},${y}`}
      fill={TEAL_LIGHT}
      stroke="#0b1518"
      strokeWidth={1}
      style={{ filter: 'drop-shadow(0 0 4px rgba(111,211,196,.85))' }}
    />
  );
};

// ── Season Spotlight — "station readout" panel below the tuner dial ───────────
const SeasonSpotlight = ({ season }: { season: SeasonEntry }) => {
  const isShort = season.season === shortestSeason.season;
  return (
    <div style={{
      position: 'relative',
      borderRadius: 6,
      background: 'linear-gradient(180deg,rgba(16,26,28,.95),rgba(8,14,16,.96))',
      border: `1px solid rgba(201,162,75,.22)`,
      padding: 'clamp(12px,2vw,16px) clamp(16px,2.4vw,22px)',
      boxShadow: '0 8px 28px rgba(0,0,0,.4)',
    }}>
      {/* date range (left) + tier / duration (right) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.lead, color: tc.default, letterSpacing: '.01em' }}>
          {fmtDateRange(season.startDate, season.finishDate)}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.small, color: tc.teal, fontWeight: 600 }}>
            {tierLabel(season.tierMin, season.tierMax)}
          </span>
          <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(201,162,75,.5)' }} />
          <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.small, color: isShort ? tc.gold : tc.muted, fontStyle: isShort ? 'italic' : 'normal' }}>
            {season.durationDays}d{isShort ? ' · short season' : ''}
          </span>
        </div>
      </div>

      {/* metrics — single row on wide screens, wraps on narrow */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(116px,1fr))', gap: '10px 14px' }}>
        {[
          { label: 'Battles', value: fmtApprox(estBattles(season.battles)), color: GOLD_LIGHT },
          { label: 'Captains', value: fmtApprox(season.players), color: TEAL_LIGHT },
          { label: 'Avg Games', value: fmtApprox(season.avgGamesPerPlayer), color: tc.strong },
          { label: 'Avg Damage', value: fmtApprox(season.avgDamagePerBattle), color: tc.strong },
          { label: 'Win Rate', value: `${season.winrate}%`, color: tc.strong },
          { label: 'Max Battles', value: fmtApprox(season.maxBattlesOnePlayer), color: tc.default },
        ].map((stat) => (
          <div key={stat.label}>
            <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.16em', textTransform: 'uppercase', color: tc.muted, marginBottom: 3 }}>
              {stat.label}
            </div>
            <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: fs.h6, color: stat.color, lineHeight: 1.1 }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Season Dial geometry — one equal band per calendar year (2017 → 2026) ─────
// The dial is divided into equal-width year bands. A season is placed by its
// date WITHIN its year's band, so every tick — and the needle — sits visibly
// inside the correct year. Year labels are centred in each band: evenly spaced
// like a real tuner scale, with no edge clipping and no label collisions.
const dialFirstYear = seasons[0].year;
const dialLastYear = seasons[seasons.length - 1].year;
const dialYearSpan = dialLastYear - dialFirstYear + 1;
const datePos = (iso: string) => {
  const d = new Date(iso + 'T00:00:00Z');
  const y = d.getUTCFullYear();
  const startOfYear = Date.UTC(y, 0, 1);
  const startOfNext = Date.UTC(y + 1, 0, 1);
  const within = (d.getTime() - startOfYear) / (startOfNext - startOfYear);
  const band = Math.min(dialYearSpan - 1, Math.max(0, y - dialFirstYear));
  return (band + within) / dialYearSpan;
};
const seasonDialPoints = seasons.map((s) => ({ season: s.season, t: datePos(s.startDate) }));
const yearDialBands = Array.from({ length: dialYearSpan }, (_, i) => ({
  year: dialFirstYear + i,
  start: i / dialYearSpan,
}));

// ── Season Dial — retro radio-tuner timeline ──────────────────────────────────
interface SeasonDialProps {
  seasons: SeasonEntry[];
  selectedSeason: SeasonEntry;
  onSelect: (s: SeasonEntry) => void;
}

const SeasonDial = ({ seasons, selectedSeason, onSelect }: SeasonDialProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const n = seasons.length;
  const idx = Math.max(0, seasons.findIndex((s) => s.season === selectedSeason.season));
  const needlePct = datePos(selectedSeason.startDate) * 100;
  const isShort = selectedSeason.season === shortestSeason.season;

  const pickFromX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    let best = 0;
    let bestD = Infinity;
    seasonDialPoints.forEach((p, i) => {
      const d = Math.abs(p.t - t);
      if (d < bestD) { bestD = d; best = i; }
    });
    const s = seasons[best];
    if (s && s.season !== selectedSeason.season) onSelect(s);
  };

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pickFromX(e.clientX);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging.current) pickFromX(e.clientX);
  };
  const onUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onKey = (e: React.KeyboardEvent) => {
    let ni = idx;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') ni = Math.max(0, idx - 1);
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') ni = Math.min(n - 1, idx + 1);
    else if (e.key === 'Home') ni = 0;
    else if (e.key === 'End') ni = n - 1;
    else return;
    e.preventDefault();
    const s = seasons[ni];
    if (s) onSelect(s);
  };

  return (
    <div style={{
      position: 'relative',
      borderRadius: 8,
      background: 'linear-gradient(180deg,#0d191c 0%,#091316 58%,#0b1518 100%)',
      border: '1px solid rgba(201,162,75,.28)',
      boxShadow: '0 12px 34px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.05)',
      padding: 'clamp(16px,2.6vw,22px) clamp(16px,3vw,26px) clamp(12px,2vw,16px)',
    }}>
      <Brackets opacity={0.3} />

      {/* tuned-station readout + hint */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 'clamp(16px,2.6vw,22px)', flexWrap: 'wrap' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'baseline', gap: 'clamp(8px,1.4vw,12px)', flexWrap: 'wrap',
          padding: '8px clamp(12px,1.8vw,16px)', borderRadius: 5,
          background: 'radial-gradient(130% 200% at 0% 0%, rgba(201,162,75,.12), rgba(4,8,10,.55))',
          border: '1px solid rgba(201,162,75,.22)',
          boxShadow: 'inset 0 2px 9px rgba(0,0,0,.6)',
        }}>
          <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: fs.h5, color: GOLD_LIGHT, textShadow: '0 0 14px rgba(236,210,139,.5)', lineHeight: 1 }}>
            S{selectedSeason.season}
          </span>
          <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: fs.lead, color: tc.strong, lineHeight: 1.1, textShadow: '0 0 10px rgba(242,232,207,.22)' }}>
            {selectedSeason.name}
          </span>
          <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.16em', color: tc.teal, textTransform: 'uppercase' }}>
            {tierLabel(selectedSeason.tierMin, selectedSeason.tierMax)}{isShort ? ' · short' : ''}
          </span>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.22em', textTransform: 'uppercase', color: tc.muted }}>
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: TEAL_LIGHT, boxShadow: '0 0 9px rgba(111,211,196,.85)', animation: 'cbDialGlow 2.4s ease-in-out infinite' }} />
          Drag to tune
        </span>
      </div>

      {/* dial face */}
      <div
        ref={trackRef}
        className="cb-dial"
        role="slider"
        tabIndex={0}
        aria-label="Season tuner — drag or use arrow keys to select a season"
        aria-valuemin={seasons[0].season}
        aria-valuemax={seasons[n - 1].season}
        aria-valuenow={selectedSeason.season}
        aria-valuetext={`Season ${selectedSeason.season}, ${selectedSeason.name}, ${selectedSeason.year}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onKeyDown={onKey}
        style={{ position: 'relative', height: 74, cursor: 'pointer', touchAction: 'none', borderRadius: 4 }}
      >
        {/* year-band divider hairlines (internal boundaries only) */}
        {yearDialBands.map((b, i) => (i === 0 ? null : (
          <span key={`div-${b.year}`} aria-hidden="true" style={{
            position: 'absolute', left: `${b.start * 100}%`, top: 6, height: 30, width: 1,
            background: 'rgba(201,162,75,.13)',
          }} />
        )))}

        {/* baseline scale */}
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 22, height: 2, borderRadius: 2, background: 'linear-gradient(90deg,rgba(201,162,75,.12),rgba(201,162,75,.42) 50%,rgba(201,162,75,.12))' }} />

        {/* season ticks — placed by date within their year band */}
        {seasonDialPoints.map((p) => {
          const active = p.season === selectedSeason.season;
          return (
            <span key={p.season} aria-hidden="true" style={{
              position: 'absolute', left: `${p.t * 100}%`, top: 24, transform: 'translateX(-50%)',
              width: active ? 2 : 1, height: active ? 13 : 8, borderRadius: 1,
              background: active ? GOLD_LIGHT : 'rgba(201,162,75,.4)',
              boxShadow: active ? '0 0 8px rgba(236,210,139,.85)' : 'none',
            }} />
          );
        })}

        {/* year labels — at each year's START boundary, so a year's seasons sit
            to the RIGHT of its number. The lit year follows the needle's real
            position (by date), not the data's year tag — so a season that
            starts in e.g. Dec lights the year it actually started in. */}
        {yearDialBands.map((b, i) => {
          const nextStart = yearDialBands[i + 1]?.start ?? 1;
          const lit = needlePct / 100 >= b.start && needlePct / 100 < nextStart;
          const atEdge = i === 0;
          return (
            <span key={b.year} aria-hidden="true" className="cb-dial-year" style={{
              position: 'absolute', left: `${b.start * 100}%`, top: 42,
              transform: atEdge ? 'none' : 'translateX(-50%)',
              fontFamily: "'Barlow',sans-serif", letterSpacing: '.03em', whiteSpace: 'nowrap',
              color: lit ? tc.gold : tc.muted, fontWeight: lit ? 600 : 400,
              transition: 'color .14s, font-weight .14s',
            }}>{b.year}</span>
          );
        })}

        {/* tuning needle */}
        <div aria-hidden="true" style={{ position: 'absolute', left: `${needlePct}%`, top: 0, height: 42, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', transition: 'left .14s cubic-bezier(.4,0,.2,1)' }}>
          <span style={{ width: 12, height: 12, transform: 'rotate(45deg)', background: 'linear-gradient(135deg,#f8e7b2,#c49a3f)', boxShadow: '0 0 12px rgba(236,210,139,.95)', borderRadius: 1 }} />
          <span style={{ flex: 1, width: 2, marginTop: 1, background: 'linear-gradient(180deg,#f8e7b2,rgba(201,162,75,.15))', boxShadow: '0 0 10px rgba(236,210,139,.7)' }} />
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const CB = () => {
  const [selectedSeason, setSelectedSeason] = useState<SeasonEntry>(
    seasons.find((s) => s.season === 11) ?? seasons[seasons.length - 1],
  );

  const pageTitle = 'World of Warships Clan Battles History (EU) — Every Season Since 2017 | TFD';
  const pageDesc =
    'Complete history of World of Warships EU-server Clan Battles: 33 seasons, ' +
    'an estimated 2.4 million battles, 2017–2026. Interactive charts covering battles, captains, average damage, ' +
    'and tier data for every WoWS Clan Battles season.';
  const canonicalUrl = `${SITE_URL}/cb`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'World of Warships EU Clan Battles History — Seasons 1–33',
    description: pageDesc,
    url: canonicalUrl,
    image: `${SITE_URL}/og-cb.png`,
    creator: {
      '@type': 'Organization',
      name: 'The Flying Dutchman (TFD)',
      url: SITE_URL,
    },
    temporalCoverage: '2017/2026',
    spatialCoverage: 'EU server — World of Warships',
    license: 'https://creativecommons.org/licenses/by/4.0/',
    keywords: [
      'World of Warships',
      'WoWS',
      'Clan Battles',
      'EU server',
      'season history',
      'WoWS statistics',
    ],
  };

  const handleSeasonHover = (data: { activePayload?: { payload: SeasonEntry }[] } | null) => {
    if (data?.activePayload?.[0]?.payload) {
      setSelectedSeason(data.activePayload[0].payload as SeasonEntry);
    }
  };

  const chartsData = chartData as SeasonEntry[];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="theme-color" content="#05090c" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The Flying Dutchman" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={`${SITE_URL}/og-cb.png`} />
        <meta property="og:image:secure_url" content={`${SITE_URL}/og-cb.png`} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="World of Warships EU Clan Battles history — 33 seasons, 2017–2026" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={`${SITE_URL}/og-cb.png`} />
        <meta name="twitter:image:alt" content="World of Warships EU Clan Battles history — 33 seasons, 2017–2026" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

        {/* Season tuner dial styles */}
        <style>{`
          .cb-dial:focus-visible {
            outline: 2px solid rgba(236,210,139,.6);
            outline-offset: 4px;
          }
          /* tuner year-scale labels: 15px desktop (token body15), 12px floor on mobile (token caption) */
          .cb-dial-year { font-size: 15px; }
          @media (max-width: 560px) { .cb-dial-year { font-size: 12px; } }
          @keyframes cbDialGlow {
            0%, 100% { opacity: .5; }
            50% { opacity: 1; }
          }
        `}</style>
      </Helmet>

      <div style={{
        position: 'relative',
        minHeight: '100vh',
        background:
          'radial-gradient(900px 600px at 50% 0%, rgba(46,125,116,.14), transparent 60%),' +
          'linear-gradient(180deg,#070d10 0%,#05090c 40%,#04080b 100%)',
        color: tc.strong,
        fontFamily: "'Barlow',sans-serif",
        overflowX: 'hidden',
      }}>

        {/* Atmosphere */}
        <div aria-hidden="true" style={{ position: 'fixed', inset: '-10% -10% auto -10%', height: '70vh', pointerEvents: 'none', zIndex: 0, opacity: .35, background: 'radial-gradient(55% 70% at 25% 15%, rgba(58,150,140,.18), transparent 60%)', filter: 'blur(50px)', animation: 'fogdrift 30s ease-in-out infinite alternate' }} />
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, background: 'radial-gradient(130% 100% at 50% 30%, transparent 50%, rgba(0,0,0,.5) 100%)' }} />

        {/* NAV */}
        <nav style={{ position: 'sticky', top: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px clamp(20px,5vw,52px)', background: 'rgba(5,9,12,.88)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,162,75,.16)', gap: 16 }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: fs.small, letterSpacing: '.14em', background: 'linear-gradient(180deg,#f8e7b2,#e6c977 38%,#b88e38 64%,#ecd28b)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>THE FLYING DUTCHMAN</span>
          </a>
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 4, background: 'linear-gradient(180deg,#ecd28b,#c49a3f)', color: '#1a1206', fontFamily: "'Barlow',sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid #8a6d2c', padding: '8px 16px', fontSize: fs.caption, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.4), 0 4px 14px rgba(201,162,75,.22)' }}>
            <DiscordIcon size={14} />
            Discord
          </a>
        </nav>

        <div style={{ position: 'relative', zIndex: 1 }}>

          {/* ── HERO ── tighter spacing, data-first */}
          <header style={{ textAlign: 'center', padding: 'clamp(36px,5vw,64px) clamp(20px,5vw,40px) clamp(24px,3vw,40px)' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.32em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>
              <EUFlag size={18} />
              EU Server · World of Warships · 2017 – 2026
            </div>
            <h1 style={{ fontFamily: "'Cinzel',serif", fontWeight: 400, fontSize: 'clamp(32px,6vw,76px)', lineHeight: 1.06, margin: '0 0 8px', letterSpacing: '.03em', background: 'linear-gradient(180deg,#f8e7b2 0%,#e6c977 38%,#b88e38 64%,#ecd28b 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,.4))' }}>
              Clan Battles
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, margin: '12px 0 16px' }}>
              <span style={{ height: 1, width: 60, background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.6))' }} />
              <span style={{ width: 6, height: 6, transform: 'rotate(45deg)', background: GOLD, boxShadow: '0 0 10px rgba(201,162,75,.7)' }} />
              <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(14px,2.2vw,22px)', color: tc.default, letterSpacing: '.06em' }}>Nine Years of Battle</span>
              <span style={{ width: 6, height: 6, transform: 'rotate(45deg)', background: GOLD, boxShadow: '0 0 10px rgba(201,162,75,.7)' }} />
              <span style={{ height: 1, width: 60, background: 'linear-gradient(90deg,rgba(201,162,75,.6),transparent)' }} />
            </div>
            <p style={{ fontSize: fs.body, lineHeight: 1.7, color: tc.muted, maxWidth: 560, margin: '0 auto 8px' }}>
              Figures cover <strong style={{ color: tc.default }}>World of Warships EU-server Clan Battles</strong>, Season 1 (Oct 2017) through Season 33 (2026). Every sortie, every captain — in the numbers.
            </p>
          </header>

          {/* ── HEADLINE STATS ── */}
          <section style={{ padding: '0 clamp(20px,5vw,40px) clamp(32px,5vw,56px)' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(168px,1fr))', gap: 1, background: 'rgba(201,162,75,.15)', border: '1px solid rgba(201,162,75,.2)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.45)' }}>
                {[
                  { value: fmtApprox(estBattles(totalBattles)), label: 'Battles', gold: true },
                  { value: '33', label: 'Seasons (2017–2026)', gold: false },
                  { value: fmtApprox(totalDamage), label: 'Total Damage Dealt', gold: true },
                  { value: `S${peakParticipation.season}`, label: 'Peak Season', gold: false, sub: `${peakParticipation.name} · ${fmtApprox(peakParticipation.players)} captains` },
                ].map((s) => (
                  <div key={s.label} style={{ background: 'linear-gradient(180deg,#0c171a,#091215)', padding: 'clamp(20px,2.5vw,28px) 18px', textAlign: 'center' }}>
                    <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 'clamp(22px,3vw,36px)', lineHeight: 1.1, color: s.gold ? undefined : TEAL_LIGHT, ...(s.gold ? { background: 'linear-gradient(180deg,#f8e7b2,#e6c977 38%,#b88e38 64%,#ecd28b)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' } : {}) }}>
                      {s.value}
                    </div>
                    <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.2em', textTransform: 'uppercase', color: tc.muted, marginTop: 6 }}>{s.label}</div>
                    {s.sub && <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted, marginTop: 3 }}>{s.sub}</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── METHODOLOGY / ACCURACY NOTE ── */}
          <section style={{ padding: '0 clamp(20px,5vw,40px) clamp(28px,4vw,48px)' }}>
            <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', gap: 14, alignItems: 'flex-start', padding: '16px 20px', borderRadius: 8, background: 'rgba(79,179,165,.05)', border: '1px solid rgba(79,179,165,.2)' }}>
              <span aria-hidden="true" style={{ color: TEAL_LIGHT, flexShrink: 0, marginTop: 1 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
              </span>
              <p style={{ margin: 0, fontFamily: "'Barlow',sans-serif", fontSize: fs.body, lineHeight: 1.7, color: tc.default }}>
                <strong style={{ color: tc.strong }}>Every number here is an estimate.</strong> Wargaming doesn't publish Clan Battles statistics, so we build these figures from the public profiles of the EU captains who took part. Hidden profiles always leave a slice out, which puts us at roughly <strong style={{ color: tc.teal }}>95%</strong> of the true totals. So read the figures as “about this many” rather than exact counts — and trust the season-to-season trends most of all, since they all rest on the same consistent method.
              </p>
            </div>
          </section>

          {/* ── CHARTS AREA ── */}
          <section style={{ padding: '0 clamp(20px,5vw,40px) clamp(32px,5vw,64px)' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto' }}>

              <Divider />

              {/* ── What are Clan Battles? (SEO prose) ── */}
              <div style={{ maxWidth: 760, margin: '0 auto clamp(32px,5vw,56px)', textAlign: 'center' }}>
                <p style={{ fontSize: fs.body, lineHeight: 1.8, color: tc.muted }}>
                  <strong style={{ color: tc.default }}>Clan Battles</strong> is World of Warships' premier competitive mode — 7-vs-7 clan-vs-clan fleet engagements played each season at a fixed ship tier. Seasons run for six to eight weeks (except for a handful of short seasons), and the data below covers the complete EU-server record from the first season in October 2017 to the most recent sortie.
                </p>
              </div>

              {/* ── Season tuner: dial across the full width, readout below ── */}
              <div style={{ marginBottom: 'clamp(44px,7vw,80px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.32em', textTransform: 'uppercase', color: tc.gold, fontWeight: 600 }}>
                    Season Tuner
                  </div>
                  <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,rgba(201,162,75,.3),transparent)' }} />
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted }}>
                    nine years, season by season
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <SeasonDial
                    seasons={chartsData}
                    selectedSeason={selectedSeason}
                    onSelect={setSelectedSeason}
                  />
                  <SeasonSpotlight season={selectedSeason} />
                </div>
              </div>

              {/* ── CHART 1: Battles + Players (ComposedChart) ── */}
              <div style={{ marginBottom: 'clamp(32px,5vw,60px)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.38em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>
                    Season by Season
                  </div>
                  <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(20px,3.4vw,36px)', margin: '0 0 8px', color: tc.strong, letterSpacing: '.02em' }}>
                    Battles &amp; Participation
                  </h2>
                  <p style={{ fontSize: fs.body, color: tc.muted, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
                    Estimated battles (bars, left axis) and captains taking part (line, right axis) per WoWS Clan Battles season. Season 11 — <em>Northern Waters</em> — remains the peak, at {fmtApprox(estBattles(peakParticipation.battles))} battles and {fmtApprox(peakParticipation.players)} captains. Hover a season for full details.
                  </p>
                </div>

                <div style={{ position: 'relative', padding: 'clamp(20px,3vw,32px) clamp(16px,3vw,28px) clamp(16px,2vw,24px)', borderRadius: 8, background: 'linear-gradient(180deg,rgba(14,24,26,.9),rgba(7,12,14,.95))', border: '1px solid rgba(201,162,75,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.5)' }}>
                  <Brackets />
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 24, marginBottom: 12, paddingLeft: 4, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: fs.caption, color: tc.muted, fontFamily: "'Barlow',sans-serif" }}>
                      <span style={{ display: 'inline-block', width: 14, height: 10, borderRadius: 2, background: 'rgba(201,162,75,.7)' }} />
                      Battles (×1,000)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: fs.caption, color: tc.muted, fontFamily: "'Barlow',sans-serif" }}>
                      <span style={{ display: 'inline-block', width: 20, height: 2, background: TEAL, borderRadius: 1 }} />
                      Captains taking part
                    </div>
                  </div>
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                    <div style={{ minWidth: 420 }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartsData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }} onMouseMove={handleSeasonHover}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,162,75,.1)" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: tc.muted, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={{ stroke: 'rgba(201,162,75,.2)' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            yAxisId="battles"
                            orientation="left"
                            tick={{ fill: tc.gold, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => `${v}K`}
                            width={40}
                          />
                          <YAxis
                            yAxisId="players"
                            orientation="right"
                            tick={{ fill: tc.teal, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => fmtNum(v)}
                            width={40}
                          />
                          <Tooltip
                            content={
                              <BrandTooltip
                                formatters={{
                                  'Battles (K)': (v) => `≈${v.toLocaleString()}K`,
                                  'Captains': (v) => fmtApprox(v),
                                }}
                              />
                            }
                            cursor={{ fill: 'rgba(201,162,75,.06)' }}
                          />
                          <ReferenceLine
                            yAxisId="battles"
                            x={`S${shortestSeason.season}`}
                            stroke="rgba(201,162,75,.35)"
                            strokeDasharray="4 3"
                            label={<S25Label />}
                          />
                          <ReferenceLine
                            yAxisId="battles"
                            x={`S${selectedSeason.season}`}
                            stroke={TEAL_LIGHT}
                            strokeWidth={2}
                            strokeOpacity={0.9}
                            isFront
                            ifOverflow="extendDomain"
                            label={<TunedNeedleLabel />}
                          />
                          <Bar
                            yAxisId="battles"
                            dataKey="battlesK"
                            name="Battles (K)"
                            fill="rgba(201,162,75,.65)"
                            radius={[2, 2, 0, 0]}
                          />
                          <Line
                            yAxisId="players"
                            type="monotone"
                            dataKey="players"
                            name="Captains"
                            stroke={TEAL}
                            strokeWidth={2}
                            dot={{ r: 2.5, fill: TEAL, strokeWidth: 0 }}
                            activeDot={{ r: 4, fill: TEAL_LIGHT }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CHART 2: Average Damage per Battle ── */}
              <div style={{ marginBottom: 'clamp(32px,5vw,60px)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.38em', color: tc.teal, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>
                    Meta Evolution
                  </div>
                  <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(20px,3.4vw,36px)', margin: '0 0 8px', color: tc.strong, letterSpacing: '.02em' }}>
                    Average Damage per Battle
                  </h2>
                  <p style={{ fontSize: fs.body, color: tc.muted, maxWidth: 580, margin: '0 auto', lineHeight: 1.6 }}>
                    Power creep and tier shifts written in damage numbers. Tier 6 seasons average ~30 K, Tier 8 seasons ~38–41 K, and Tier 10 regularly tops 60 K. Season 25 — <em>Polar Bear</em> — spiked to {fmtApprox(peakAvgDmgSeason.avgDamagePerBattle)} despite being a short season; Tier 10 in a compressed window amplified the average.
                  </p>
                </div>

                <div style={{ position: 'relative', padding: 'clamp(20px,3vw,32px) clamp(16px,3vw,28px) clamp(16px,2vw,24px)', borderRadius: 8, background: 'linear-gradient(180deg,rgba(14,24,26,.9),rgba(7,12,14,.95))', border: '1px solid rgba(79,179,165,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.5)' }}>
                  <Brackets color={TEAL} opacity={0.5} />
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                    <div style={{ minWidth: 420 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={chartsData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }} onMouseMove={handleSeasonHover}>
                          <defs>
                            <linearGradient id="dmgGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={TEAL} stopOpacity={0.28} />
                              <stop offset="95%" stopColor={TEAL} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(79,179,165,.1)" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: tc.muted, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={{ stroke: 'rgba(79,179,165,.2)' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            tick={{ fill: tc.teal, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => fmtNum(v)}
                            width={44}
                          />
                          <Tooltip
                            content={
                              <BrandTooltip
                                formatters={{
                                  'Avg Damage': (v) => fmtApprox(v),
                                }}
                              />
                            }
                            cursor={{ stroke: 'rgba(79,179,165,.25)', strokeWidth: 1 }}
                          />
                          <ReferenceLine
                            x={`S${shortestSeason.season}`}
                            stroke="rgba(201,162,75,.35)"
                            strokeDasharray="4 3"
                            label={<S25Label />}
                          />
                          <ReferenceLine
                            x={`S${selectedSeason.season}`}
                            stroke={TEAL_LIGHT}
                            strokeWidth={2}
                            strokeOpacity={0.9}
                            isFront
                            ifOverflow="extendDomain"
                            label={<TunedNeedleLabel />}
                          />
                          <Area
                            type="monotone"
                            dataKey="avgDamagePerBattle"
                            name="Avg Damage"
                            stroke={TEAL}
                            strokeWidth={2}
                            fill="url(#dmgGrad)"
                            dot={{ r: 2.5, fill: TEAL, strokeWidth: 0 }}
                            activeDot={{ r: 4, fill: TEAL_LIGHT }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CHART 3: Avg Games per Player ── */}
              <div style={{ marginBottom: 'clamp(32px,5vw,60px)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.38em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>
                    Engagement
                  </div>
                  <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(20px,3.4vw,36px)', margin: '0 0 8px', color: tc.strong, letterSpacing: '.02em' }}>
                    Average CB Games per Captain
                  </h2>
                  <p style={{ fontSize: fs.body, color: tc.muted, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
                    How many Clan Battles the average participant played each season — a measure of engagement depth beyond raw headcount. Ranges from {fmtApprox(Math.min(...seasons.map(s => s.avgGamesPerPlayer)))} to {fmtApprox(Math.max(...seasons.map(s => s.avgGamesPerPlayer)))} games per captain. S25's low value reflects its shortened {shortestSeason.durationDays}-day run.
                  </p>
                </div>

                <div style={{ position: 'relative', padding: 'clamp(20px,3vw,32px) clamp(16px,3vw,28px) clamp(16px,2vw,24px)', borderRadius: 8, background: 'linear-gradient(180deg,rgba(14,24,26,.9),rgba(7,12,14,.95))', border: '1px solid rgba(201,162,75,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.5)' }}>
                  <Brackets />
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                    <div style={{ minWidth: 420 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={chartsData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }} onMouseMove={handleSeasonHover}>
                          <defs>
                            <linearGradient id="gppGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={GOLD} stopOpacity={0.22} />
                              <stop offset="95%" stopColor={GOLD} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,162,75,.1)" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: tc.muted, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={{ stroke: 'rgba(201,162,75,.2)' }}
                            tickLine={false}
                            interval={2}
                          />
                          <YAxis
                            tick={{ fill: tc.gold, fontFamily: "'Barlow',sans-serif", fontSize: fs.caption }}
                            axisLine={false}
                            tickLine={false}
                            width={36}
                          />
                          <Tooltip
                            content={
                              <BrandTooltip
                                formatters={{
                                  'Games / Captain': (v) => fmtApprox(v),
                                }}
                              />
                            }
                            cursor={{ stroke: 'rgba(201,162,75,.2)', strokeWidth: 1 }}
                          />
                          <ReferenceLine
                            x={`S${shortestSeason.season}`}
                            stroke="rgba(201,162,75,.35)"
                            strokeDasharray="4 3"
                            label={<S25Label />}
                          />
                          <ReferenceLine
                            x={`S${selectedSeason.season}`}
                            stroke={TEAL_LIGHT}
                            strokeWidth={2}
                            strokeOpacity={0.9}
                            isFront
                            ifOverflow="extendDomain"
                            label={<TunedNeedleLabel />}
                          />
                          <Area
                            type="monotone"
                            dataKey="avgGamesPerPlayer"
                            name="Games / Captain"
                            stroke={GOLD}
                            strokeWidth={2}
                            fill="url(#gppGrad)"
                            dot={{ r: 2.5, fill: GOLD, strokeWidth: 0 }}
                            activeDot={{ r: 4, fill: GOLD_LIGHT }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── S25 outlier note ── */}
              <div style={{ maxWidth: 760, margin: '0 auto clamp(32px,5vw,56px)', padding: '14px 20px', borderRadius: 6, background: 'rgba(201,162,75,.06)', border: '1px solid rgba(201,162,75,.2)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ color: tc.gold, fontFamily: "'Barlow',sans-serif", fontSize: fs.small, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>S25</span>
                <p style={{ margin: 0, fontFamily: "'Barlow',sans-serif", fontSize: fs.body, color: tc.muted, lineHeight: 1.65 }}>
                  <strong style={{ color: tc.default }}>Polar Bear (S25)</strong> was a short ~{shortestSeason.durationDays}-day season ({fmtDateRange(shortestSeason.startDate, shortestSeason.finishDate)}), compared to the usual 41–70 days. This explains the lower battle counts and fewer games per captain, while the Tier {shortestSeason.tierMax} bracket pushed average damage to a season record.
                </p>
              </div>

              {/* ── SEASON RECORDS ── */}
              <Divider />
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.38em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Records</div>
                <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 500, fontSize: 'clamp(20px,3.4vw,36px)', margin: '0 0 8px', color: tc.strong, letterSpacing: '.02em' }}>Season Records</h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16, marginBottom: 'clamp(32px,5vw,60px)' }}>
                {[
                  {
                    label: 'Most Battles',
                    value: fmtApprox(estBattles(peakBattlesSeason.battles)),
                    sub: `Season ${peakBattlesSeason.season} · ${peakBattlesSeason.name} (${peakBattlesSeason.year})`,
                    color: GOLD,
                    season: peakBattlesSeason,
                  },
                  {
                    label: 'Peak Participation',
                    value: `${fmtApprox(peakParticipation.players)} captains`,
                    sub: `Season ${peakParticipation.season} · ${peakParticipation.name} (${peakParticipation.year})`,
                    color: TEAL,
                    season: peakParticipation,
                  },
                  {
                    label: 'Highest Avg Damage',
                    value: fmtApprox(peakAvgDmgSeason.avgDamagePerBattle),
                    sub: `Season ${peakAvgDmgSeason.season} · ${peakAvgDmgSeason.name} (${peakAvgDmgSeason.year})`,
                    color: GOLD_LIGHT,
                    season: peakAvgDmgSeason,
                  },
                ].map((card) => (
                  <button
                    key={card.label}
                    onClick={() => setSelectedSeason(card.season as SeasonEntry)}
                    style={{ position: 'relative', padding: '24px 22px', borderRadius: 6, background: 'linear-gradient(180deg,rgba(14,24,26,.9),rgba(8,14,16,.94))', border: `1px solid rgba(${card.color === TEAL || card.color === TEAL_LIGHT ? '79,179,165' : '201,162,75'},.2)`, boxShadow: '0 16px 40px rgba(0,0,0,.4)', cursor: 'pointer', textAlign: 'left', color: 'inherit', fontFamily: 'inherit', transition: 'border-color 0.18s', width: '100%' }}
                  >
                    <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.24em', textTransform: 'uppercase', color: tc.muted, marginBottom: 8 }}>{card.label}</div>
                    <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 'clamp(16px,2.2vw,24px)', color: card.color, marginBottom: 6, lineHeight: 1.2 }}>{card.value}</div>
                    <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted, lineHeight: 1.5 }}>{card.sub}</div>
                  </button>
                ))}
              </div>

            </div>
          </section>

          {/* ── CTA ── */}
          <section style={{ padding: '0 clamp(20px,5vw,40px) clamp(60px,8vw,100px)' }}>
            <div style={{ maxWidth: 780, margin: '0 auto' }}>
              <div style={{ position: 'relative', borderRadius: 10, background: 'linear-gradient(180deg,rgba(18,30,32,.96),rgba(7,12,14,.97))', border: '1px solid rgba(201,162,75,.28)', padding: 'clamp(36px,5vw,64px) clamp(24px,5vw,52px)', boxShadow: 'inset 0 0 0 1px rgba(201,162,75,.05), 0 34px 80px rgba(0,0,0,.55)', textAlign: 'center', overflow: 'hidden' }}>
                <div aria-hidden="true" style={{ position: 'absolute', top: -70, left: '50%', transform: 'translateX(-50%)', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle,rgba(58,150,140,.2),transparent 70%)', pointerEvents: 'none' }} />

                {[
                  { top: 14, left: 14, bt: true, bl: true },
                  { top: 14, right: 14, bt: true, br: true },
                  { bottom: 14, left: 14, bb: true, bl: true },
                  { bottom: 14, right: 14, bb: true, br: true },
                ].map((p, i) => (
                  <span key={i} aria-hidden="true" style={{ position: 'absolute', top: (p as { top?: number }).top, left: (p as { left?: number }).left, right: (p as { right?: number }).right, bottom: (p as { bottom?: number }).bottom, width: 18, height: 18, borderTop: (p as { bt?: boolean }).bt ? `1.5px solid ${GOLD}` : undefined, borderBottom: (p as { bb?: boolean }).bb ? `1.5px solid ${GOLD}` : undefined, borderLeft: (p as { bl?: boolean }).bl ? `1.5px solid ${GOLD}` : undefined, borderRight: (p as { br?: boolean }).br ? `1.5px solid ${GOLD}` : undefined, opacity: .65 }} />
                ))}

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, letterSpacing: '.4em', color: tc.gold, textTransform: 'uppercase', fontWeight: 600, marginBottom: 18 }}>
                    Hoist the Colours
                  </div>
                  <h2 style={{ fontFamily: "'Cinzel',serif", fontWeight: 400, fontSize: 'clamp(24px,4vw,48px)', lineHeight: 1.1, margin: '0 0 18px', color: tc.strong, letterSpacing: '.02em' }}>
                    Wanna play Clan Battles?
                  </h2>
                  <p style={{ fontSize: fs.body, lineHeight: 1.7, color: tc.muted, maxWidth: 500, margin: '0 auto 28px' }}>
                    Nine years of Clan Battles history — but the best sorties are still ahead. Join a friendly clan with regular World of Warships Clan Battles, nightly divisions, and a crew that genuinely enjoys the game. Look no further.
                  </p>

                  <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                    <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="tfd-btn-gold" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, borderRadius: 5, background: 'linear-gradient(180deg,#ecd28b,#c49a3f)', color: '#1a1206', fontFamily: "'Barlow',sans-serif", fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid #8a6d2c', padding: '14px 28px', fontSize: fs.small, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.5), 0 10px 28px rgba(201,162,75,.28)' }}>
                      <DiscordIcon size={16} />
                      Join our Discord
                    </a>
                    <a href="/" className="tfd-btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 28px', borderRadius: 5, background: 'rgba(58,150,140,.06)', color: tc.default, fontFamily: "'Barlow',sans-serif", fontWeight: 600, fontSize: fs.small, letterSpacing: '.12em', textTransform: 'uppercase', textDecoration: 'none', border: '1px solid rgba(79,179,165,.4)' }}>
                      Meet the Fleet
                      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </a>
                  </div>

                  <p style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted, margin: 0 }}>
                    Three clans — TFD, TFX, TFO — one flag. EU server, friendly crew, since 2017.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── FOOTER ── */}
          <footer style={{ borderTop: '1px solid rgba(201,162,75,.1)', padding: '32px clamp(20px,5vw,40px)', textAlign: 'center' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <a href="/" style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: fs.small, letterSpacing: '.14em', textDecoration: 'none', background: 'linear-gradient(180deg,#f8e7b2,#e6c977 38%,#b88e38 64%,#ecd28b)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>
                THE FLYING DUTCHMAN
              </a>
              <div style={{ fontFamily: "'Barlow',sans-serif", fontSize: fs.caption, color: tc.muted }}>
                Data: TFD stats service · EU server · Seasons 1–33 · Not affiliated with Wargaming.net
              </div>
            </div>
          </footer>

        </div>
      </div>
    </>
  );
};

export default CB;
